export function formatUsd(v: number): string {
  return `$${v.toFixed(4)}`;
}

export function formatTokens(v: number): string {
  return v.toLocaleString('en-US');
}

/**
 * **얼마나 오래됐나** — 사람 말로 (지시137 ①, gui 결정6455).
 *
 * 지시 원문: *«해시만 보이면 사람이 못 읽는다 — «얼마나 오래됐나» 를 사람 말로 함께 낸다»*.
 *
 * ## ⚠ 크기만 말하고 **판정은 안 한다**
 *
 * *«6일 전»* 은 사실이고 *«뒤처졌다»* 는 판정이다. 이 함수는 **앞의 것만** 한다 —
 * 뒤처졌는지는 서버가 정한다(지시137 ③: 화면이 혼자 하면 판정이 두 벌이 된다).
 * axis-api 가 그 판정을 `same`/`differs(+direction)`/`unknown` 셋으로 낼 참이고,
 * 그쪽이 «stale» 이라는 낱말을 **일부러 거부한** 이유도 같다 — 플랫폼이 되돌려졌으면
 * **노드가 더 새것일 수 있다.**
 *
 * ## 앞으로 간 시각을 «0일» 로 접지 않는다
 *
 * 브라우저 시계가 서버보다 뒤에 있으면 미래 시각이 온다. 그때 «방금» 이라고 하면
 * **거짓말**이고, 음수 일수를 그리면 사람이 못 읽는다. 그래서 `undefined` 를 돌려주고
 * **부르는 쪽이 그 자리를 비운다** — 「모르는 것을 아는 척하지 않는다」.
 */
export function formatAge(iso: string | undefined, now: number = Date.now()): string | undefined {
  if (iso === undefined || iso === '') return undefined;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return undefined;
  const ms = now - then;
  /* 미래이거나 같은 순간이면 «오래됨» 을 말할 것이 없다. */
  if (ms < 0) return undefined;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * **초를 사람 말로** (지시137, gui 결정6465).
 *
 * `formatAge` 와 **문구를 나눠 쓴다** — 두 벌로 지으면 «6 days ago» 와 «6 days» 가 갈린다.
 *
 * ⚠ **`behindSeconds` 로 만든다.** 브라우저 시계로 `agentBuild` 를 견주면 안 된다
 * 〔axis-api 실측〕: 노드가 05:17 에 올라가고 기준이 05:38 에 다시 구워지면 «21분» 인데
 * **판은 어긋나 있다.** 나이만 보고 «최신이겠지» 로 읽는 그 자리다.
 *
 * ⚠ **음수는 «모른다» 가 아니다** — `ahead`(노드가 더 새것)일 때 온다. 크기만 말하고
 * 방향은 `verdict` 가 말한다. 그래서 절댓값으로 옮긴다.
 */
export function formatSpan(seconds: number | undefined): string | undefined {
  if (seconds === undefined || !Number.isFinite(seconds)) return undefined;
  const s = Math.abs(Math.round(seconds));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function formatDuration(startedAt?: string, endedAt?: string): string {
  if (!startedAt) return '—';
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const ms = Math.max(0, end - start);
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  /*
   * ★ **한 시간이 넘으면 분으로 말하지 않는다** (결정5429).
   *
   * 〔글로 읽어 알았다〕 `run-001` 이 `running` 인 채 여러 날 남아 있는데 목록의
   * 지속시간이 **`12348m 58s`** 로 떴다. 사람이 그것을 «8일 반» 으로 옮기려면
   * 머리로 나눠야 한다 — 그리고 **그 줄이야말로 눈에 띄어야 하는 줄**이다.
   * 끝나지 않은 실행은 대개 **끊긴 실행**이다(env 결정3712 가 단계 쪽에서 같은 말을 했다).
   *
   * ⚠ 한 시간 아래는 **그대로 둔다** — 흔한 경우의 글자가 바뀌면 읽는 습관과
   * 기존 검사가 같이 흔들린다. 늘어나는 쪽만 손본다.
   */
  /*
   * ⚠ **초를 끝까지 남긴다** (결정3440 이 잡아 줬다).
   *
   * 처음엔 `8d 13h` 로 줄였다가 `elapsedKeepsMoving` 이 붉었다 — 그 검사는 시계를
   * **3초** 밀고 글자가 **바뀌는지**를 본다. 도는 런의 경과가 굳어 보이면
   * «도는 중» 과 «화면이 멈춤» 이 같은 그림이 되기 때문이다(결정3440).
   * 시·일만 보이면 한 시간에 한 번만 바뀐다 — 그 성질이 깨진다.
   *
   * 그래서 **읽기 쉬운 단위를 더하되 초는 안 뺀다.** 둘 다 지킨다.
   */
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return `${h}h ${remM}m ${rem}s`;
  return `${Math.floor(h / 24)}d ${h % 24}h ${remM}m ${rem}s`;
}

/**
 * **초를 사람 말로** (결정2423).
 *
 * `formatDuration` 은 ISO 두 개를 받는다. 서버가 **초로** 주는 자리가 있어
 * (`QueueHealth.oldestWaitingSeconds`) 그 갈래를 따로 둔다 — 부르는 쪽에서
 * 초를 가짜 ISO 로 만들어 넘기면 **두 벌째 서식**이 생긴다.
 *
 * ⚠ 잘라 쓰는 규칙을 `formatDuration` 과 **같게** 맞췄다(60초 미만은 초, 그 위는 분·초).
 * 두 자리가 다르게 보이면 같은 화면에서 같은 길이가 달리 읽힌다.
 */
export function formatSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

/**
 * 사람이 읽는 시각대 (운영자 지시, 결정1433).
 *
 * > *"보고·브리프·시트·결정 문서에 적는 시각은 **KST**. DB·로그·커밋에 저장되는 시각은
 * >  **UTC 그대로.** 저장을 바꾸라는 것이 아니다 — **사람이 읽는 자리만 KST** 다."*
 *
 * 콘솔은 사람이 읽는 자리다. 그리고 지시88 이 **상태 보고를 이 화면으로 옮겼으므로**
 * 여기가 곧 보고 화면이다.
 */
const READING_TZ = 'Asia/Seoul';
const READING_TZ_LABEL = 'KST';

/**
 * **시각에 단위를 붙인다** (운영자 지시, 결정1433).
 *
 * 종전에는 `toLocaleString()` 이었다 — **브라우저 시각대**를 쓰고 **이름표가 없다.**
 * 둘 다 오늘 실제로 아팠다:
 *
 * ```
 * 이름표 없음   운영자 지시 그대로다 — *"숫자만 적으면 다음 사람이 UTC 로 읽는다"*
 * 브라우저 TZ   이 배포의 컨테이너는 **UTC** 다〔실측〕. 그러면 화면이 UTC 를 그리면서
 *               **아무 말도 안 한다** — 읽는 사람은 자기 시각(KST)으로 읽고 **9시간
 *               어긋난다.** 오늘 하루 혼란의 절반이 *"어느 시각이냐"* 였다
 * ```
 *
 * **저장은 안 건드린다.** 서버가 주는 ISO(UTC)를 그대로 받고, **그리는 자리에서만**
 * 옮긴다 — 지시가 명시적으로 그렇게 갈랐다.
 *
 * ⚠ 시각대를 **한 곳에 박아 둔다.** 운영자가 다른 시각대를 원하면 이 두 상수만 바뀐다 —
 * 화면마다 흩어 두면 그때 어디를 고쳐야 하는지 아무도 모른다.
 */
export function formatTimestamp(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  /* 못 읽는 값을 조용히 "Invalid Date" 로 그리지 않는다 — 없는 것과 깨진 것은 다르다. */
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString('en-CA', { timeZone: READING_TZ, hour12: false })} ${READING_TZ_LABEL}`;
}

/** 시:분:초만. 같은 시각대·같은 이름표를 쓴다 — 한 화면에서 둘이 다르면 못 믿는다. */
export function formatClock(iso?: string | number): string {
  if (iso === undefined || iso === null || iso === '') return '—';
  const d = new Date(iso);
  /* 못 읽는 값은 지어내지 않고 그대로 돌려준다 — 없는 것과 깨진 것은 다르다. */
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleTimeString('en-GB', { timeZone: READING_TZ, hour12: false })} ${READING_TZ_LABEL}`;
}
